import type {
  CrmEntityAssociationMapping,
  FixedValueMapping,
} from '@/lib/csv-import/api';

import { getStageEntityTypeForSource } from './mapping-constants';

/**
 * Finds the stage-typed `crm_entity_association_mappings` entry for a file's
 * source entity type, if one exists. Mirrors the filter in
 * `analyzer.py::_resolve_stage_names` (`target_entity_type_name` matching the
 * source's stage entity type).
 */
export function findStageAssociationMapping(
  crmEntityAssociationMappings: CrmEntityAssociationMapping[],
  sourceFileId: string,
  entityTypeName: string
): CrmEntityAssociationMapping | undefined {
  const stageEntityType = getStageEntityTypeForSource(entityTypeName);
  if (!stageEntityType) {
    return undefined;
  }
  return crmEntityAssociationMappings.find(
    m =>
      String(m.source_file_id) === sourceFileId &&
      m.target_entity_type_name === stageEntityType
  );
}

/**
 * Resolves the pipeline stage entity id a previewed row will receive, mirroring
 * `analyzer.py:4091-4146` (`_resolve_stage_names`) exactly so the preview cannot
 * disagree with execute. Deliberately does NOT trim/lowercase the raw CSV value —
 * the backend reads `raw_data.get(stage_column, "")` and does an exact,
 * case-sensitive `value_mappings` lookup, so normalizing here would make preview
 * disagree with execute.
 */
export function resolvePreviewStageEntityId(
  stageMapping: CrmEntityAssociationMapping | undefined,
  row: Record<string, string> | undefined,
  defaultStage: string | null | undefined
): string | null {
  // Trimmed to match the backend's `_get_default_stage_for_file`
  // (apps/csv_imports/services/executor.py:1518-1528), which returns
  // `configured.strip()` — not `_resolve_stage_names`, which governs the
  // column/value_mappings branch below.
  const fallback =
    defaultStage && defaultStage.trim() ? defaultStage.trim() : null;
  const sourceColumn = stageMapping?.source_column ?? '';
  const valueMappings = stageMapping?.value_mappings ?? {};

  if (!sourceColumn) {
    return fallback;
  }

  const raw = row?.[sourceColumn] ?? '';
  if (!raw) {
    return fallback;
  }

  if (!Object.prototype.hasOwnProperty.call(valueMappings, raw)) {
    return fallback;
  }

  const mapped = valueMappings[raw];
  if (mapped === '') {
    return null;
  }
  return mapped;
}

/**
 * Resolves the owner/visibility a previewed row's created entity will receive
 * from `fixed_value_mappings`. Mirrors the association-type naming convention
 * used by `executor.py::_resolve_fixed_value_mappings`
 * (`<entity_type>_to_owner`, `<entity_type>_visible_to_org`,
 * `<entity_type>_to_visible_user`).
 */
export function resolvePreviewOwnerVisibility(
  fixedValueMappings: FixedValueMapping[],
  sourceFileId: string,
  entityTypeName: string
): { ownerUserId: number | null; visibility: 'public' | 'private' | null } {
  const ownerAssoc = `${entityTypeName}_to_owner`;
  const orgVisAssoc = `${entityTypeName}_visible_to_org`;
  const userVisAssoc = `${entityTypeName}_to_visible_user`;

  const fileMappings = fixedValueMappings.filter(
    m => String(m.source_file_id) === sourceFileId
  );

  const ownerRow = fileMappings.find(
    m => m.association_type_name === ownerAssoc
  );
  const visRow = fileMappings.find(
    m =>
      m.association_type_name === orgVisAssoc ||
      m.association_type_name === userVisAssoc
  );

  let visibility: 'public' | 'private' | null = null;
  if (visRow) {
    visibility =
      visRow.association_type_name === userVisAssoc ? 'private' : 'public';
  }

  return {
    ownerUserId: ownerRow?.target_user_id ?? null,
    visibility,
  };
}
